import { useEffect, useState } from 'react';
import { isStandalone, isIOSSafari, isMobile } from '../utils/platform';
import { getDeferredInstallPrompt, clearDeferredInstallPrompt, onInstallPromptAvailable } from '../utils/installPrompt';

const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';
const BLUE  = 'var(--signal)';

const DISMISS_KEY = 'truflow_a2hs_dismissed';

export function hasUserDismissedInstallPrompt() {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setUserDismissedInstallPrompt() {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // Storage disabled (private mode, quota) — the prompt just resurfaces
    // next time, which is the safe direction to fail in.
  }
}

// 'ios' | 'android' | null. null covers: already installed, desktop (this is a
// mobile-first ask — see CLAUDE.md), or a Chromium mobile browser that hasn't
// fired beforeinstallprompt yet this load.
function useInstallPlatform() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredInstallPrompt());

  useEffect(() => onInstallPromptAvailable(setDeferredPrompt), []);

  if (isStandalone()) return { platform: null, deferredPrompt: null };
  if (isIOSSafari()) return { platform: 'ios', deferredPrompt: null };
  if (isMobile() && deferredPrompt) return { platform: 'android', deferredPrompt };
  return { platform: null, deferredPrompt: null };
}

// True when the auto-surfaced banner has something to show: eligible platform
// and not previously dismissed. Settings' manual entry point ignores dismissal
// by design — that row exists specifically for someone who dismissed once and
// changed their mind — so it checks platform eligibility on its own instead.
export function useShouldShowInstallBanner() {
  const { platform } = useInstallPlatform();
  return platform !== null && !hasUserDismissedInstallPrompt();
}

function ShareIcon() {
  // iOS's share glyph: an upward arrow escaping an open box, so the copy
  // below ("tap the Share icon") has something to point at instead of
  // describing a symbol the user has to go find on their own.
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ stroke: BLUE }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4" />
      <path d="M7.5 8.5L12 4l4.5 4.5" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

// The platform-specific body shared by the inline banner and the Settings
// modal. `dense` tightens spacing for the modal, which has no surrounding
// card padding to borrow from.
function InstallPromptBody({ platform, deferredPrompt, onInstalled, dense }) {
  const [installing, setInstalling] = useState(false);

  async function handleInstallClick() {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      // One-shot event either way — accepted or dismissed, Chrome won't
      // reuse it, so holding onto it would only risk a stale second call.
      clearDeferredInstallPrompt();
      setInstalling(false);
      onInstalled?.();
    }
  }

  if (platform === 'ios') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          flexShrink: 0, width: 32, height: 32, borderRadius: 8,
          backgroundColor: 'var(--signal-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ShareIcon />
        </div>
        <p style={{ margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          Tap the <strong style={{ color: TEXT1 }}>Share</strong> icon in Safari's toolbar, then{' '}
          <strong style={{ color: TEXT1 }}>Add to Home Screen</strong>.
        </p>
      </div>
    );
  }

  if (platform === 'android') {
    return (
      <button
        onClick={handleInstallClick}
        disabled={installing}
        style={{
          width: dense ? '100%' : 'auto',
          backgroundColor: installing ? 'var(--signal-disabled)' : BLUE,
          color: 'var(--on-signal)',
          border: 'none', borderRadius: 10,
          padding: '10px 16px', fontSize: 13.5, fontWeight: 600,
          cursor: installing ? 'default' : 'pointer',
        }}
      >
        {installing ? 'Opening…' : 'Install app'}
      </button>
    );
  }

  // Reached only from the Settings modal when the Chromium install event
  // isn't available this load (already used, or never fired). Points at the
  // browser's own menu rather than showing nothing.
  return (
    <p style={{ margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
      Open your browser menu and look for <strong style={{ color: TEXT1 }}>Add to Home Screen</strong> or{' '}
      <strong style={{ color: TEXT1 }}>Install app</strong>.
    </p>
  );
}

// Dismissible card for the one auto-surfaced placement (onboarding Summary,
// after the user has seen their first reserve number). Renders nothing on any
// platform this doesn't apply to — callers can mount it unconditionally.
export function InstallPromptBanner() {
  const { platform, deferredPrompt } = useInstallPlatform();
  const [dismissed, setDismissed] = useState(hasUserDismissedInstallPrompt);

  if (platform === null || dismissed) return null;

  function handleDismiss() {
    setUserDismissedInstallPrompt();
    setDismissed(true);
  }

  return (
    <div style={{
      backgroundColor: 'var(--sunk)',
      borderRadius: 12,
      padding: 14,
      // Own margins and alignment rather than the host screen's, so a centered
      // container (PaymentSuccess) doesn't centre the instruction copy and no
      // host has to reserve a gap that would strand whitespace on the platforms
      // where this renders nothing.
      margin: '16px 0 24px',
      textAlign: 'left',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: TEXT1 }}>
          Add TruFlow to your Home Screen
        </p>
        <InstallPromptBody
          platform={platform}
          deferredPrompt={deferredPrompt}
          onInstalled={handleDismiss}
        />
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 18, color: TEXT3, lineHeight: 1, padding: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}

// Settings row that opens this. Hidden once installed — see useShouldShowInstallRow.
export function useShouldShowInstallRow() {
  return !isStandalone() && (isIOSSafari() || isMobile());
}

// On-demand modal for Settings — the permanent way back in for anyone who
// dismissed the banner or simply never saw it. Deliberately does not check
// hasUserDismissedInstallPrompt(): that flag only silences the automatic
// banner, never this explicit entry point.
export function InstallPromptModal({ onClose }) {
  const { platform, deferredPrompt } = useInstallPlatform();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 320, width: '100%', backgroundColor: 'var(--card)', borderRadius: 'var(--r-card)', padding: 20 }}
      >
        <p style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: TEXT1 }}>
          Add TruFlow to your Home Screen
        </p>
        <InstallPromptBody
          platform={platform}
          deferredPrompt={deferredPrompt}
          onInstalled={onClose}
          dense
        />
        <button
          onClick={onClose}
          style={{
            width: '100%', marginTop: 16,
            background: 'none', border: 'none',
            color: TEXT3, fontSize: 13, cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
